import * as yup from 'yup';
import type { GovFormFileItem } from '@gov-design-system-ce/components/dist/types/components/gov-form/file/gov-form-file.types';
import type { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import type { TranslationMessage } from '@gov-nx/core/service/translations/translation.types';
import { getValues, hasOwnProperty } from '@gov-nx/core/types/helpers.types';
import { MB } from './constants';

export type FileUploadMessage = Extract<
	TranslationMessage<LocalizeNameSpaceTypes.Form>,
	{ key: `nahrani-souboru.status.${string}` }
>;

export enum FileState {
	error = 'error',
	uploading = 'uploading',
	validating = 'validating',
	success = 'success',
}

export interface FileUploadNative {
	uploadType: 'native';
	id: string;
	uri: string;
	name: string;
	type?: string;
	size: number;
}

export type FileUploadWeb = GovFormFileItem;

export const isFileUploadItemNative = (
	file: FileUploadItem
): file is FileUploadItemNative =>
	hasOwnProperty(file, 'uploadType') && file.uploadType === 'native';

export const isFileUploadItemWeb = (
	file: FileUploadItem
): file is FileUploadItemWeb => !isFileUploadItemNative(file);

export const isFileUploadNative = (
	file: FileUpload
): file is FileUploadNative =>
	hasOwnProperty(file, 'uploadType') && file.uploadType === 'native';

export type FileUpload = FileUploadNative | FileUploadWeb;

type FileUploadItemCommon = {
	name: string;
	size: number;
	type?: string;
	state: FileState;
	message?: FileUploadMessage;
	fileId?: string;
	index: number;
	showLargeFileLoader?: boolean;
};

export type FileUploadItemWeb = FileUploadItemCommon & FileUploadWeb;
export type FileUploadItemNative = FileUploadItemCommon & FileUploadNative;

export type FileUploadItem = FileUploadItemWeb | FileUploadItemNative;

export type InitialUploadItemState = Pick<
	FileUploadItem,
	'state' | 'message' | 'showLargeFileLoader'
>;

export const fileShapeNative = () =>
	yup.object({
		id: yup.string().required(),
		name: yup.string().required(),
		size: yup.number().required(),
		type: yup.string().optional(),
		state: yup.string().oneOf(getValues(FileState)).required(),
		message: yup.mixed<FileUploadMessage>().optional(),
		index: yup.number().required(),
		uri: yup.string().required(),
		uploadType: yup.string().oneOf(['native']).required(),
	});

export const fileShapeWeb = () =>
	yup.object({
		id: yup.string().required(),
		file: yup.mixed<File>().required(),
		acceptValid: yup.boolean().required(),
		sizeValid: yup.boolean().required(),
		name: yup.string().required(),
		size: yup.number().required(),
		type: yup.string().optional(),
		state: yup.string().oneOf(getValues(FileState)).required(),
		index: yup.number().required(),
	});

export type FileUploadProps = {
	type?: string | string[];
	extensions: string[];
	maxFileSize: number;
	maxSumFileSize: number;
	maxAttachments: number;
	multiple: boolean;
	onFileRemoved?: (fileId: FileUploadItem) => void;
	onAllFilesUploaded?: (files: FileUploadItem[]) => Promise<FileUploadItem[]>;
};

export const DefaultUploadParameters: FileUploadProps = {
	extensions: [
		'.pdf',
		'.xml',
		'.fo',
		'.zfo',
		'.htm',
		'.html',
		'.odp',
		'.odt',
		'.ods',
		'.txt',
		'.rtf',
		'.doc',
		'.docx',
		'.xls',
		'.xlsx',
		'.ppt',
		'.pptx',
		'.jpeg',
		'.jpg',
		'.jp2',
		'.jfif',
		'.png',
		'.tif',
		'.tiff',
		'.gif',
		'.mpg',
		'.mpeg',
		'.mpeg1',
		'.mpeg2',
		'.mp1',
		'.mp2',
		'.mp3',
		'.wav',
		'.isdoc',
		'.isdocx',
		'.edi',
		'.dwg',
		'.shp',
		'.dbf',
		'.shx',
		'.prj',
		'.qix',
		'.sbn',
		'.sbx',
		'.dgn',
		'.gml',
		'.gfs',
		'.xsd',
		'.cer',
		'.crt',
		'.csv',
		'.der',
		'.p7b',
		'.p7c',
		'.p7f',
		'.p7m',
		'.p7s',
		'.tsr',
		'.tst',
	],
	maxFileSize: 20 * MB,
	maxSumFileSize: 20 * MB,
	maxAttachments: 1,
	multiple: false,
};
